<template>
    <div class="member-information field-spacing-container">
        <form-wrapper :validator="$v.member" class="form-wrapper">
            <div class="information-box">
                <div class="md-layout">
                    <div class="md-layout-item">
                        <form-group name="memberCode" label="Code">
                            <md-input v-model="member.memberCode" :disabled="!isAllowedToUpdate" />
                        </form-group>
                    </div>
                    <div class="md-layout-item">
                        <form-group name="firstName" label="First Name">
                            <md-input v-model="member.firstName" :disabled="!isAllowedToUpdate" />
                        </form-group>
                    </div>
                    <div class="md-layout-item">
                        <form-group name="lastName" label="Last Name">
                            <md-input v-model="member.lastName" :disabled="!isAllowedToUpdate" />
                        </form-group>
                    </div>
                </div>

                <div class="md-layout">
                    <div class="md-layout-item">
                        <form-group name="email" label="Email Address">
                            <md-input v-model="member.email" />
                        </form-group>
                    </div>
                    <div class="md-layout-item">
                        <form-group name="phone" label="Phone">
                            <md-input v-model="member.phone" :disabled="!isAllowedToUpdate" />
                        </form-group>
                    </div>
                </div>
            </div>
            <div class="information-box" v-if="skillList.length">
                <div class="md-layout">
                    <div class="md-layout-item">
                        <div class="skillfield-container">
                            <label for="skills" v-if="member.skills.length">Skills</label>
                            <multiselect
                                v-model="member.skills"
                                :options="skillList"
                                :multiple="true"
                                :close-on-select="true"
                                :disabled="!isAllowedToUpdate"
                                placeholder="Pick skills"
                            ></multiselect>
                        </div>
                    </div>
                </div>
            </div>
            <div class="information-box">
                <div class="md-layout">
                    <div class="md-layout-item">
                        <div class="skillfield-container">
                            <label for="serviceAreas">Service Areas</label>
                            <multiselect
                                v-model="member.serviceAreas"
                                :options="serviceAreasList"
                                :multiple="true"
                                :close-on-select="true"
                                placeholder="Pick service areas"
                            ></multiselect>
                        </div>
                    </div>
                </div>
            </div>
            <div class="information-box" v-if="capacityDefinitions.length">
                <div class="md-layout">
                    <div class="md-layout-item">
                        <capacity-inputs
                            v-for="(capacity, index) in capacityDefinitions"
                            :key="index"
                            :capacity-definition="capacity"
                            :initial-value="capacityValues[capacity.type]"
                            :disabled="!isAllowedToUpdate"
                            @changed="handleCapacityChanged"
                        />
                    </div>
                </div>
            </div>
            <div class="information-box">
                <div class="md-layout-item md-xsmall-size-100 md-size-34">
                    <form-group name="emissionPerDistance" label="C02 per km/mile">
                        <md-input type="number" v-model.number="member.emissionPerDistance" />
                    </form-group>
                </div>
            </div>
            <div class="information-box">
                <div class="md-layout">
                    <div v-if="showTeamRegion" class="md-layout-item">
                        <form-group name="team-region" label="Team Region">
                            <team-region-options
                                mode="update"
                                :selected="member.teamRegionId"
                                :show-auto="false"
                                :enabled="canEditOwnTeamRegion && isAllowedToUpdate"
                                @selectedOption="handleTeamRegionChanged"
                            />
                        </form-group>
                    </div>
                </div>
            </div>
            <div class="information-box">
                <div v-if="!hasTeam" class="md-layout">
                    <div class="md-layout-item">
                        <form-group name="company" label="Company">
                            <md-input v-model="member.company" :disabled="!isAllowedToUpdate" />
                        </form-group>
                    </div>
                    <div class="md-layout-item">
                        <form-group name="industry" label="Industry">
                            <md-input v-model="member.industry" :disabled="!isAllowedToUpdate" />
                        </form-group>
                    </div>
                </div>
                <div class="md-layout">
                    <div class="md-layout-item">
                        <form-group name="role" label="Team Role">
                            <md-select
                                v-model="member.role"
                                @md-selected="getSelectedRole"
                                :disabled="user.publicUserId === member.publicUserId || !isAllowedToUpdate"
                            >
                                <md-option
                                    v-for="memberRole in memberRoles"
                                    :key="memberRole.id"
                                    :value="memberRole.name"
                                >
                                    {{ memberRole.name }}
                                </md-option>
                            </md-select>
                        </form-group>
                    </div>
                    <div class="md-layout-item vehicle-box">
                        <label>Routing Speed:</label>
                        <md-radio v-model="vehicleType" value="Bike" :disabled="!isAllowedToUpdate">Bike</md-radio>
                        <md-radio v-model="vehicleType" value="Car" :disabled="!isAllowedToUpdate">Car</md-radio>
                        <md-radio v-model="vehicleType" value="Walk" :disabled="!isAllowedToUpdate">Walk</md-radio>
                        <md-radio v-model="vehicleType" value="Truck" :disabled="!isAllowedToUpdate">Truck</md-radio>
                    </div>
                </div>
            </div>
            <div class="information-box">
                <div class="md-layout">
                    <div class="md-layout-item">
                        <form-group name="role" label="Mobile Location Tracking Behaviour" class="md-field-split">
                            <gps-collection-dropdown
                                :selected="member.gpsDataCollectionRule"
                                @selectedOption="getSelectedTrackingBehaviour"
                                :disabled="!isAllowedToUpdate"
                            />
                        </form-group>
                    </div>
                    <div class="md-layout-item md-xsmall-size-100 md-size-25">
                        <form-group
                            name="defaultTripStartTime"
                            label="Start Time"
                            class="timer-field hide-timer-clear-button"
                        >
                            <time-picker
                                :time="startTime"
                                :all-time-options="false"
                                :clear-none="true"
                                :disabled="!isAllowedToUpdate"
                                @selectedTime="getStartTime"
                            />
                        </form-group>
                    </div>
                    <div class="md-layout-item md-xsmall-size-100 md-size-25">
                        <form-group name="defaultTripEndTime" label="End Time" class="timer-field">
                            <time-picker
                                :time="endTime"
                                :all-time-options="false"
                                :disabled="!isAllowedToUpdate"
                                @selectedTime="getEndTime"
                            />
                        </form-group>
                    </div>
                </div>
            </div>
            <div class="information-box">
                <div class="md-layout">
                    <div class="md-layout-item md-xsmall-size-100 md-size-25">
                        <form-group name="defaultStopDurationMinutes" label="Default Stop Duration (in Minutes)">
                            <md-input type="number" v-model.number="member.defaultStopDurationMinutes" />
                        </form-group>
                    </div>
                    <div class="md-layout-item md-xsmall-size-100 md-size-35">
                        <form-group name="stopDurationMultiplier" label="Stop Duration Multiplier">
                            <md-input type="number" v-model.number="member.stopDurationMultiplier" />
                        </form-group>
                    </div>
                </div>
            </div>
            <div class="information-box">
                <div class="md-layout">
                    <div class="md-layout-item">
                        <google-autocomplete
                            label="Start Address"
                            ref="startaddress"
                            id="startaddress"
                            classname="form-control autocomplete-input"
                            placeholder=""
                            v-model="startLocation"
                            @placechanged="handleStartLocationChange"
                            :disabled="!isAllowedToUpdate"
                        ></google-autocomplete>
                    </div>
                </div>
            </div>

            <div class="information-box">
                <div class="md-layout">
                    <div class="md-layout-item">
                        <google-autocomplete
                            label="End Address"
                            ref="endaddress"
                            id="endaddress"
                            classname="form-control autocomplete-input"
                            placeholder=""
                            v-model="endLocation"
                            @placechanged="handleEndLocationChange"
                            :disabled="!isAllowedToUpdate"
                        ></google-autocomplete>
                    </div>
                </div>
            </div>

            <!-- <payment-method-inputs :detail="member" /> -->

            <div class="information-box">
                <div class="md-layout">
                    <div class="md-layout-item">
                        <md-field>
                            <label>Bio</label>
                            <md-textarea v-model="member.bio" :disabled="!isAllowedToUpdate" />
                        </md-field>
                    </div>
                </div>
            </div>
            <div class="information-box">
                <div class="md-layout">
                    <div class="md-layout-item custom-switch">
                        <label class="label-text">
                            Is this user a driver?
                            <md-icon class="icon-info">info</md-icon>
                            <md-tooltip class="tooltip-width" md-direction="right">
                                This setting is used to determine whether the user can be assigned stops or shipments.
                            </md-tooltip>
                        </label>
                        <md-switch v-model="isDriver"></md-switch>
                    </div>
                </div>
            </div>
            <div class="information-box">
                <div class="md-layout">
                    <div class="md-layout-item custom-switch">
                        <label class="label-text">Enable Stop/Shipment Offer Notifications</label>
                        <md-switch v-model="canReceiveOffers"></md-switch>
                    </div>
                </div>
            </div>
            <div class="information-box" v-if="member.hasTeamAppPermissions">
                <div class="md-layout">
                    <div class="md-layout-item md-medium-size-75 md-xsmall-size-100 md-size-75">
                        <h5 class="app-title">App Permissions</h5>
                        <label class="md-layout-item md-size-35 md-form-label">Views</label>
                        <div class="md-layout-item md-size-100 layout-item">
                            <div>
                                <md-checkbox v-model="member.appPermissions.views.items" @change="itemScanningChanged">
                                    Item scanning
                                </md-checkbox>
                            </div>
                            <div>
                                <md-checkbox
                                    v-model="member.appPermissions.views.itemChecklist"
                                    @change="itemChecklistChanged"
                                >
                                    Item checklist
                                </md-checkbox>
                            </div>
                            <div
                                class="item-checklist-scanning-container"
                                v-show="member.appPermissions.views.itemChecklist"
                            >
                                <md-checkbox v-model="member.appPermissions.views.itemChecklistScanning">
                                    With scanning
                                </md-checkbox>
                            </div>
                            <div>
                                <md-checkbox
                                    v-model="member.appPermissions.qrFrequentAddress"
                                    @change="qrFrequentAddressChanged"
                                >
                                    QR frequent address
                                </md-checkbox>
                            </div>
                        </div>
                    </div>
                    <div class="md-layout-item md-medium-size-75 md-xsmall-size-100 md-size-75">
                        <label class="md-layout-item md-size-35 md-form-label">Default view</label>
                        <div class="md-layout-item md-size-65">
                            <md-field>
                                <md-select v-model="member.appPermissions.views.default" name="defaultview">
                                    <md-option value="">Not Selected</md-option>
                                    <md-option value="items">Item scanning</md-option>
                                    <md-option value="itemChecklist">Item checklist</md-option>
                                </md-select>
                            </md-field>
                        </div>
                    </div>
                </div>
            </div>
            <div class="form-actions">
                <md-button v-if="isAllowedToUpdate" class="md-primary pull-right" @click="validateForm">
                    Update Profile
                </md-button>
            </div>
        </form-wrapper>
    </div>
</template>

<script>
import {
    handleRequests,
    isPhoneValid,
    showErrorMessage,
    showTeamRegionControl,
    minStopDuration,
    maxStopDuration,
    isStopDurationNull,
    compareProperties
} from '@/helpers';
import { GeneralMixin } from '@/mixins/GeneralMixin';
import { mapGetters } from 'vuex';
import { required, email, requiredIf } from 'vuelidate/lib/validators';
import { GPS_DATA_COLLECTION_RULE_CONSTANTS, APPLY_RATES_TO, INVOICE_LINE_TYPES } from '@/utils/constants';
import { GoogleAutocomplete, TimePicker, GpsCollectionDropdown, CapacityInputs, TeamRegionOptions } from '@/components';
import _ from 'lodash';

export default {
    name: 'MemberDetails',
    mixins: [GeneralMixin],
    components: {
        GoogleAutocomplete,
        TimePicker,
        GpsCollectionDropdown,
        CapacityInputs,
        TeamRegionOptions
    },
    props: {
        data: {
            type: Object,
            required: true
        },
        isAllowedToUpdate: {
            type: Boolean,
            required: true
        }
    },
    data() {
        return {
            member: {
                skills: [],
                teamRegionId: null,
                serviceAreas: [],
                vehicleCapacity: {},
                memberCode: null,
                appPermissions: {
                    qrFrequentAddress: false,
                    views: {
                        items: false,
                        itemChecklist: false,
                        itemChecklistScanning: false,
                        default: ''
                    }
                },
                hasTeamAppPermissions: false,
                customFields: {}
            },
            memberRoles: [],
            oldRole: null,
            gpsDataCollectionOptions: GPS_DATA_COLLECTION_RULE_CONSTANTS,
            skillList: [],
            serviceAreasList: [],
            startLocation: {},
            endLocation: {},
            showUploadButton: false,
            oldMemberRole: null,
            isLoaded: false,
            capacityDefinitions: [],
            capacityValues: {},
            oldEmail: null,
            rateGroupId: -1,
            oldAccountNumber: null,
            businessRegistrationNumberLabel: 'Business Registration Number',
            applyRatesTo: APPLY_RATES_TO,
            applyRatesToValue: [1, 2, 4],
            invoiceLineOptions: INVOICE_LINE_TYPES,
            vehicleType: null,
            canReceiveOffers: false,
            isDriver: false,
            tripStartTime: null,
            startTime: null,
            endTime: null
        };
    },
    validations: {
        member: {
            firstName: {
                required
            },
            email: {
                required,
                email
            },
            role: {
                required
            },
            defaultTripStartTime: {
                required
            },
            defaultTripEndTime: {
                required:
                    // eslint-disable-next-line func-names
                    requiredIf(function () {
                        return (
                            this.member.gpsDataCollectionRule === 'Business Hours' && !this.member.defaultTripEndTime
                        );
                    })
            },
            phone: {
                phoneValid: isPhoneValid
            },
            defaultStopDurationMinutes: {
                minValue_customerStopDuration: minStopDuration,
                maxValue_customerStopDuration: maxStopDuration, // max duration value of 24 hours,
                numeric_duration: isStopDurationNull
            },
            stopDurationMultiplier: {
                minValue_Multiplier(duration) {
                    if (duration === '' || duration === null) 
                        return true;

                    if (duration < 0.1) 
                        return false;

                    return true;
                },
                numeric_duration: isStopDurationNull
            }
        }
    },
    computed: {
        ...mapGetters({
            user: 'user/user',
            hasTeam: 'user/hasTeam',
            hasAssetAccess: 'user/hasAssetAccess',
            isSupportUser: 'user/isSupportUser',
            isSupportAdminUser: 'user/isSupportAdminUser',
            canEditOwnTeamRegion: 'user/canEditOwnTeamRegion',
            teamRegions: 'team/teamRegions'
        }),
        showTeamRegion() {
            return showTeamRegionControl(
                {
                    user: this.user,
                    hasAssetAccess: this.hasAssetAccess,
                    teamRegions: this.teamRegions
                },
                this.member.teamRegionId
            );
        }
    },
    async mounted() {
        this.$_handleLoaderState(true);

        const apirole = '/api/roles';
        handleRequests(apirole).then((response) => {
            // Asset removed from the list
            this.memberRoles = response.data.data.filter((x) => x.name !== 'Asset');
        });

        this.handleMemberDetails(this.data);
        this.getSkills();
        this.getServiceAreas();

        this.$_handleLoaderState(false);
    },
    watch: {
        data: {
            handler(newVal, oldVal) {
                this.handleMemberDetails(newVal);
            },
            deep: true
        },
        vehicleType: {
            handler(newVal, oldVal) {
                this.member.vehicleType = newVal;
            }
        },
        canReceiveOffers: {
            handler(newVal, oldVal) {
                this.member.canReceiveOffers = newVal;
            }
        },
        isDriver: {
            handler(newVal, oldVal) {
                this.member.isDriver = newVal;
            }
        }
    },
    methods: {
        handleMemberDetails(data) {
            this.member = _.cloneDeep(data);
            this.startTime = this.member.defaultTripStartTime;
            this.endTime = this.member.defaultTripEndTime;
            this.vehicleType = this.member.vehicleType;
            this.canReceiveOffers = this.member.canReceiveOffers;
            this.isDriver = this.member.isDriver;
            this.startLocation = {
                address: this.member.startAddress,
                location: this.member.startLocation ? this.member.startLocation : { latitude: null, longitude: null }
            };
            this.endLocation = {
                address: this.member.endAddress,
                location: this.member.endLocation ? this.member.endLocation : { latitude: null, longitude: null }
            };
            this.oldRole = this.member.role;
            this.member.roles = [this.member.role];
            this.oldMemberRole = this.member.role;

            if (this.member.vehicleCapacity && !this.objectHasAllNaNValues(this.member.vehicleCapacity)) {
                this.capacityValues = this.member.vehicleCapacity;
            }

            this.oldEmail = this.member.email;

            this.capacityDefinitions = this.user.vehicleCapacityUnitsConfiguration || [];
        },
        objectHasAllNaNValues(data) {
            return this.hasAllNull(
                Object.fromEntries(
                    Object.entries(data).map(([key, value]) => [key, Number.isNaN(value) ? null : value])
                )
            );
        },
        hasAllNull(obj) {
            return Object.values(obj).every((value) => value === null);
        },
        validateForm() {
            this.$v.$touch();

            if (!this.$v.$invalid) {
                this.update();
            }
        },
        getSelectedRole(role) {
            if (this.oldRole !== role) {
                this.oldRole = role;
                this.member.roles.push(role);
            }
        },
        update() {
            if (!this.endLocation.address) {
                this.member.endAddress = null;
                this.member.endLocation = null;
            }

            // When user enters a blank value for defaultStopDurationMinute, it's passed to the API as an empty string. This
            // results in the API not being able to deserialise the data, resulting in a null reference exception on the server.
            // So we need to convert a blank string to null for this field.
            if (this.member.defaultStopDurationMinutes === '') 
                this.member.defaultStopDurationMinutes = null;
            if (this.member.stopDurationMultiplier === '') 
                this.member.stopDurationMultiplier = null;
            if (!this.member.serviceAreas?.length) 
                this.member.serviceAreas = null;
            if (this.member.accountsEmail === '') 
                this.member.accountsEmail = null;

            if (!this.startLocation.address) {
                this.$notify({
                    message: 'Start Address is required',
                    type: 'danger'
                });
                return;
            }
            if (this.oldEmail !== this.member.email) {
                this.$messageBox
                    .show({
                        title: 'Confirm Email and Username Change',

                        body: "This will change both this team member's email address AND username, and they will have to re-verify their email address.  Continue?",

                        buttons: ['Yes', 'No']
                    })
                    .then(async (response) => {
                        if (response.toLowerCase() === 'yes') 
                            this.updateRequest();
                    });
            } else {
                this.updateRequest();
            }
        },
        updateRequest() {
            this.member.vehicleCapacity = this.capacityValues;
            this.$emit('updated', this.member);
        },
        getSkills() {
            if (this.user.skillOptions) {
                this.skillList = this.user.skillOptions.map((x) => x.name);
            }
        },
        getServiceAreas() {
            this.serviceAreasList = this.user.serviceAreasOptions;
        },
        getStartTime(selectedTime) {
            if (selectedTime != null) {
                const { appointmentTime } = selectedTime;
                this.member.defaultTripStartTime = appointmentTime;
            } else {
                this.member.defaultTripStartTime = null;
            }
        },
        getEndTime(selectedTime) {
            if (selectedTime != null) {
                const { appointmentTime } = selectedTime;
                this.member.defaultTripEndTime = appointmentTime;
            } else {
                this.member.defaultTripEndTime = null;
            }
        },
        handleStartLocationChange(data) {
            this.member.startAddress = data.address;
            this.member.startLocation = data.location;

            if (data && data.addressComponents) {
                this.member.countryCode = data.addressComponents.countryCode;
            }
        },
        handleEndLocationChange(data) {
            this.member.endAddress = data.address;
            this.member.endLocation = data.location;
        },
        getSelectedTrackingBehaviour(value) {
            this.member.gpsDataCollectionRule = value;
        },
        handleCapacityChanged({ name, value }) {
            this.capacityValues[name] = value;
        },
        handleTeamRegionChanged(value) {
            if (!value) {
                this.member.teamRegionId = null;
            }
            this.member.teamRegionId = value;
        },
        accountNumberMasking() {
            if (
                this.member.paymentDetails.bank.accountNumber &&
                !this.member.paymentDetails.bank.accountNumber.includes('*') &&
                this.member.paymentDetails.bank.accountNumber.length > 2
            ) {
                const { accountNumber } = this.member.paymentDetails.bank;
                this.member.paymentDetails.bank.accountNumber = this.$_getMaskingText(accountNumber);
                this.oldAccountNumber = this.member.paymentDetails.bank.accountNumber;
            }
        },
        /**
         *
         * @param {string | null} deviceType - either iOS or Android. may be null if the user hasn't logged in on the mobile app yet.
         * @returns {boolean}
         */
        canRequestDeviceLog(deviceType) {
            return (this.isSupportUser || this.isSupportAdminUser) && deviceType !== null;
        },
        /**
         *
         * @param {string} publicUserId
         * @returns {Promise<void>}
         */
        async handleRequestDeviceLog(publicUserId) {
            this.$_handleLoaderState(true, 'PROCESSING...');
            const payload = {
                method: 'post'
            };
            const api = `/api/support/device-log/${publicUserId}`;
            try {
                await handleRequests(api, payload);
                this.$_handleLoaderState(false);
                this.$notify({
                    message: 'Successfully sent request for device log',
                    type: 'success'
                });
            } catch (error) {
                this.$_handleLoaderState(false);
                const message = 'Error in getting request for device log';
                showErrorMessage(this, message, error);
            }
        },
        hasChanges() {
            return !compareProperties(this.member, this.data, [
                'firstName',
                'lastName',
                'email',
                'phone',
                'skills',
                'serviceAreas',
                'teamRegionId',
                'role',
                'vehicleType',
                'vehicleCapacity',
                'gpsDataCollectionRule',
                'defaultTripStartTime',
                'defaultTripEndTime',
                'defaultStopDurationMinutes',
                'stopDurationMultiplier',
                'startAddress',
                'endAddress',
                'bio',
                'isDriver',
                'canReceiveOffers',
                'tags',
                'memberCode'
            ]);
        },
        itemScanningChanged(value) {
            if (value) {
                this.member.appPermissions.views.itemChecklist = false;
                this.member.appPermissions.qrFrequentAddress = false;
            }
        },
        itemChecklistChanged(value) {
            if (value) {
                this.member.appPermissions.views.items = false;
                this.member.appPermissions.qrFrequentAddress = false;
            }

            if (!value) {
                this.member.appPermissions.views.itemChecklistScanning = false;
            }
        },
        qrFrequentAddressChanged(value) {
            if (value) {
                this.member.appPermissions.views.items = false;
                this.member.appPermissions.views.itemChecklist = false;
                this.member.appPermissions.views.itemChecklistScanning = false;
            }
        }
    }
};
</script>

<style lang="scss" scoped>
::v-deep .custom-btn {
    margin-right: 10px;
}

.md-field.md-theme-default.md-invalid:after {
    background-color: red !important;
}

.edit-actions {
    margin-top: 20px;
    padding-right: 30px;
}

.vehicle-box {
    label {
        margin-right: 10px;
    }

    .md-radio {
        width: 65px;
    }
}

.profile-container {
    margin-top: 20px;
}

.custom-switch {
    margin-top: 22px;
    margin-bottom: 0px;

    label {
        float: left;
        font-weight: 400;
        color: #aaaaaa !important;
        line-height: 1.4;
    }

    .md-checked {
        ::v-deep .md-switch-container {
            background-color: #2b93ff !important;
        }

        ::v-deep .md-switch-thumb {
            border: 1px solid #2b93ff !important;
        }
    }

    ::v-deep .md-switch-label {
        margin-top: 0px;
    }

    .md-switch {
        margin: 0;
        margin-bottom: 10px;
        padding-left: 10px;
    }
}

.unlock-button {
    ::v-deep .md-button-content {
        color: #4caf50;
    }
}

.icon-info {
    color: #2b93ff !important;
    cursor: pointer;
    padding-bottom: 4px;
    margin-right: 15px;
}

.tooltip-width {
    max-width: 600px;
    text-align: justify;
}

.half-width {
    width: 50% !important;
}
</style>
